import React, { useState, useEffect, useRef, useCallback } from "react";
import type { SectionKey } from "../lib/types";

interface TerminalProps {
  section: SectionKey;
  onNavigate: (section: SectionKey) => void;
}

interface HistoryItem {
  type: "input" | "output" | "error";
  text: string;
}

const VALID_SECTIONS: string[] = [
  "home",
  "about",
  "skills",
  "experience",
  "projects",
  "education",
  "resume",
];

function Terminal({ section, onNavigate }: TerminalProps) {
  const [history, setHistory] = useState<HistoryItem[]>([
    { type: "output", text: '  Welcome — type "help" or click a section to navigate.' },
  ]);
  const [cmd, setCmd] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const exec = useCallback(
    (raw: string) => {
      const c = raw.trim().toLowerCase();
      const next: HistoryItem[] = [
        ...history,
        { type: "input" as const, text: `~/${section} → ${raw}` },
      ];

      if (!c) {
        setHistory(next);
        return;
      }

      if (c === "help") {
        next.push({
          type: "output" as const,
          text: "  ls · cd <section> · clear · whoami · download resume",
        });
      } else if (c === "ls") {
        next.push({
          type: "output" as const,
          text: "  about  skills  experience  projects  education  resume",
        });
      } else if (c === "clear") {
        setHistory([]);
        return;
      } else if (c === "whoami") {
        next.push({ type: "output" as const, text: `  ${section}` });
      } else if (c === "download resume") {
        next.push({ type: "output" as const, text: "  ↓ downloading resume…" });
        const a = document.createElement("a");
        a.href = "/Eric_Mwangi_Resume.pdf";
        a.download = "Eric_Mwangi_Resume.pdf";
        a.click();
      } else if (c.startsWith("cd ")) {
        const target = c.slice(3).trim();
        if (VALID_SECTIONS.includes(target)) {
          onNavigate(target as SectionKey);
          next.push({ type: "output" as const, text: `  → navigated to ${target}` });
        } else {
          next.push({ type: "error" as const, text: `  no such section: ${target}` });
        }
      } else {
        next.push({ type: "error" as const, text: `  command not found: ${c}` });
      }

      setHistory(next);
    },
    [history, section, onNavigate]
  );

  return (
    <div className="bg-bg border border-border rounded-lg overflow-hidden font-mono text-xs relative">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-bg-surface border-b border-border">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
        </div>
        <span className="text-content-muted ml-2 text-2xs">
          eric@portfolio ~/{section}
        </span>
      </div>

      {/* Scrollable output */}
      <div
        ref={scrollRef}
        className="p-4 h-56 overflow-y-auto relative"
        onClick={() => inputRef.current?.focus()}
      >
        {/* Subtle scan line */}
        <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-glow to-transparent animate-scan pointer-events-none" />

        {history.map((item: HistoryItem, i: number) => (
          <div key={i} className="mb-1 leading-relaxed">
            {item.type === "input" ? (
              <span className="text-accent">{item.text}</span>
            ) : null}
            {item.type === "output" ? (
              <span className="text-content-secondary">{item.text}</span>
            ) : null}
            {item.type === "error" ? (
              <span className="text-error">{item.text}</span>
            ) : null}
          </div>
        ))}

        {/* Prompt line */}
        <div className="flex items-center">
          <span className="text-accent whitespace-nowrap">~/{section} →&nbsp;</span>
          <input
            ref={inputRef}
            value={cmd}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCmd(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                exec(cmd);
                setCmd("");
              }
            }}
            className="flex-1 bg-transparent border-none outline-none text-content font-mono text-xs caret-accent"
            autoFocus
            spellCheck={false}
          />
          <span className="w-1.5 h-4 bg-accent animate-blink -ml-0.5" />
        </div>
      </div>
    </div>
  );
}

export default Terminal;